import React, { useEffect, useMemo, useRef, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import "./../../../styles/BulkCreateUsersModal.css";
import Button from "./../../ui/Button";

export default function BulkCreateUsersModal({ isOpen, onClose, sites = [], onSubmit }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [siteQuery, setSiteQuery] = useState("");
  const [applySiteId, setApplySiteId] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setRows([]);
    setRowErrors({});
    setGlobalError("");
    setSuccess("");
    setSaving(false);
    setSiteQuery("");
    setApplySiteId("");
    if (fileRef.current) fileRef.current.value = "";
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const siteOptions = useMemo(() => {
    const normalized = (sites || []).map((s) => ({
      label: s.sitename,
      value: String(s.id),
      level: s.level || "",
    }));

    const q = siteQuery.trim().toLowerCase();
    const filtered = !q
      ? normalized
      : normalized.filter((x) => `${x.label} ${x.level} ${x.value}`.toLowerCase().includes(q));

    return filtered.slice(0, q ? 50 : 200);
  }, [sites, siteQuery]);

  const canSubmit = useMemo(() => {
    return rows.length > 0 && !saving;
  }, [rows.length, saving]);

  const passwordRule = (pw) => {
    const s = (pw || "").trim();
    if (s.length < 12) return "Password must be at least 12 characters.";
    if (!/[A-Za-z]/.test(s)) return "Password must include a letter (A-Z).";
    if (!/\d/.test(s)) return "Password must include a number (0-9).";
    if (!/[^A-Za-z0-9]/.test(s)) return "Password must include at least one special character.";
    return "";
  };

  const emailRule = (email) => {
    const s = (email || "").trim();
    if (!s) return "Email is required.";
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    return ok ? "" : "Email format looks invalid.";
  };

  const normalizeCsvValue = (v) => String(v ?? "").trim().replace(/^"|"$/g, "");
  const parseCsvUsernames = (text) => {
    const rawLines = String(text || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (rawLines.length === 0) return { usernames: [], error: "CSV is empty." };

    const headerLine = rawLines[0];
    const headerCell = normalizeCsvValue(headerLine.split(",")[0]).toLowerCase();
    const hasHeader = headerCell === "username";
    const dataLines = hasHeader ? rawLines.slice(1) : rawLines;
    const usernames = dataLines
      .map((line) => normalizeCsvValue(line.split(",")[0]))
      .filter(Boolean);

    if (usernames.length === 0) {
      return { usernames: [], error: "No usernames found. CSV should contain a 'username' column." };
    }

    const seen = new Set();
    const unique = [];
    for (const u of usernames) {
      const key = u.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(u);
    }

    return { usernames: unique, error: "" };
  };

  const handleFile = async (file) => {
    setGlobalError("");
    setSuccess("");
    setRowErrors({});

    if (!file) return;
    const name = file.name || "";
    if (!name.toLowerCase().endsWith(".csv")) {
      setGlobalError("Please upload a .csv file.");
      return;
    }

    const text = await file.text();
    const { usernames, error } = parseCsvUsernames(text);

    if (error) {
      setGlobalError(error);
      return;
    }

    const now = Date.now();
    const nextRows = usernames.map((u, idx) => ({
      id: `${now}-${idx}`,
      username: u,
      location: "",
      password: "",
      email: "",
    }));

    setRows(nextRows);
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      return prev;
    });
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setRowErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const applyLocationToAll = () => {
    if (!applySiteId) return;
    setRows((prev) => prev.map((r) => ({ ...r, location: applySiteId })));
  };

  const validateAll = () => {
    const errors = {};
    let any = false;

    for (const r of rows) {
      const e = {};
      if (!r.username?.trim()) e.username = "Username is required.";
      if (!r.location?.trim()) e.location = "Location is required.";
      if (!r.password?.trim()) e.password = "Password is required.";
      else {
        const pw = passwordRule(r.password);
        if (pw) e.password = pw;
      }
      const em = emailRule(r.email);
      if (em) e.email = em;

      if (Object.keys(e).length > 0) {
        errors[r.id] = e;
        any = true;
      }
    }

    setRowErrors(errors);

    if (rows.length === 0) {
      setGlobalError("Upload a CSV first.");
      return false;
    }

    if (any) {
      setGlobalError("Fix the highlighted rows before submitting.");
      return false;
    }

    setGlobalError("");
    return true;
  };

  const submit = async () => {
    if (!validateAll()) return;

    setSaving(true);
    setGlobalError("");
    setSuccess("");

    try {
      const payload = rows.map((r) => ({
        username: r.username.trim(),
        location: r.location.trim(),
        password: r.password.trim(),
        email: r.email.trim(),
      }));

      await onSubmit(payload);

      setSuccess(`Submitted ${rows.length} users.`);
      setTimeout(() => onClose(), 1100);
    } catch (ex) {
      const api = ex?.body ?? ex?.response?.data ?? null;
      const errors = Array.isArray(api?.errors) ? api.errors : [];
      const messages = errors.map((e) => e?.message).filter(Boolean);
      const apiMessage = messages[0] || api?.message || ex?.message || "Failed to submit bulk users.";

      setGlobalError(messages.length ? messages.join(" • ") : apiMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-lg">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Bulk create users (Android)</h2>
            <div className="sd-subtitle">
              Upload a CSV with one column: <span className="sd-mono">username</span>. Fill in location, password and email, then submit.
            </div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {globalError && <div className="sd-alert sd-alert-bad">{globalError}</div>}
          {success && <div className="sd-alert sd-alert-good">{success}</div>}

          <div className="sd-field">
            <div className="sd-label">Upload CSV</div>
            <div className="sd-inline">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="sd-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button
                buttonStyle="btn-alternate"
                disabled={saving}
                onClick={() => {
                  if (fileRef.current) fileRef.current.value = "";
                  setRows([]);
                  setRowErrors({});
                  setGlobalError("");
                  setSuccess("");
                }}
              >
                Clear
              </Button>
            </div>
            <div className="sd-help">
              Tip: header line is optional, but preferred: <span className="sd-mono">username</span>
            </div>
          </div>
          <div className="sd-field">
            <div className="sd-label">Apply a location to all rows (optional)</div>
            <input
              className="sd-input"
              value={siteQuery}
              onChange={(e) => setSiteQuery(e.target.value)}
              placeholder="Search location…"
              autoComplete="off"
            />
            <div className="sd-dropdown">
              {siteOptions.length === 0 ? (
                <div className="sd-dropdown-empty">No matches</div>
              ) : (
                siteOptions.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`sd-dd-item ${String(applySiteId) === String(p.value) ? "is-selected" : ""}`}
                    onClick={() => {
                      setApplySiteId(p.value);
                      setSiteQuery(p.label);
                    }}
                  >
                    <span className="sd-dd-label">{p.label}</span>
                    <span className="sd-dd-meta">{p.level ? p.level : `#${p.value}`}</span>
                  </button>
                ))
              )}
            </div>

            <div className="sd-inline" style={{ marginTop: 10 }}>
              <Button buttonStyle="btn-alternate" disabled={!applySiteId || saving || rows.length === 0} onClick={applyLocationToAll}>
                Apply to all
              </Button>
              <div className="sd-help" style={{ marginTop: 0 }}>
                Selected: <strong>{applySiteId || "None"}</strong>
              </div>
            </div>
          </div>

          <div className="sd-field">
            <div className="sd-label">Users</div>

            <div className="sd-bulk-table">
              <div className="sd-bulk-head">
                <div>Username</div>
                <div>Location</div>
                <div>Password</div>
                <div>Email</div>
                <div className="sd-bulk-actions">Action</div>
              </div>

              {rows.length === 0 ? (
                <div className="sd-bulk-empty">No rows yet — upload a CSV to begin.</div>
              ) : (
                rows.map((r) => {
                  const e = rowErrors[r.id] || {};
                  return (
                    <div key={r.id} className={`sd-bulk-row ${Object.keys(e).length ? "has-row-error" : ""}`}>
                      <div>
                        <input
                          className={`sd-input ${e.username ? "has-error" : ""}`}
                          value={r.username}
                          onChange={(ev) => updateRow(r.id, { username: ev.target.value })}
                          disabled={!Array.isArray(sites) || sites.length === 0}
                          placeholder="username"
                          autoComplete="off"
                        />
                        {e.username && <div className="sd-error">{e.username}</div>}
                      </div>

                      <div>
                        <select
                          className={`sd-input ${e.location ? "has-error" : ""}`}
                          value={r.location}
                          onChange={(ev) => updateRow(r.id, { location: ev.target.value })}
                            disabled={!Array.isArray(sites) || sites.length === 0}
                        >
                          <option value="">
                          {(!Array.isArray(sites) || sites.length === 0) ? "Loading sites…" : "Select…"}
                          </option>
                          {(sites || []).map((s) => (
                            <option key={s.id} value={String(s.id)}>
                              {s.sitename}
                            </option>
                          ))}
                        </select>
                        {e.location && <div className="sd-error">{e.location}</div>}
                      </div>

                      <div>
                        <input
                          className={`sd-input ${e.password ? "has-error" : ""}`}
                          value={r.password}
                          onChange={(ev) => updateRow(r.id, { password: ev.target.value })}
                          placeholder="12+ chars incl. A-Z, 0-9, special"
                          autoComplete="new-password"
                        />
                        {e.password && <div className="sd-error">{e.password}</div>}
                      </div>

                      <div>
                        <input
                          className={`sd-input ${e.email ? "has-error" : ""}`}
                          value={r.email}
                          onChange={(ev) => updateRow(r.id, { email: ev.target.value })}
                          placeholder="name@domain.com"
                          autoComplete="off"
                        />
                        {e.email && <div className="sd-error">{e.email}</div>}
                      </div>

                      <div className="sd-bulk-actions">
                        <button
                          type="button"
                          className="sd-bulk-remove"
                          onClick={() => removeRow(r.id)}
                          aria-label="Remove row"
                          disabled={saving}
                          title="Remove row"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {rows.length > 0 && (
              <div className="sd-help" style={{ marginTop: 10 }}>
                Rows: <strong>{rows.length}</strong> • Password rules: <strong>12+ chars</strong>, includes <strong>letter</strong>, <strong>number</strong>, and <strong>special</strong>.
              </div>
            )}
          </div>
        </div>

        <div className="sd-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} buttonStyle="btn-main" disabled={!canSubmit}>
            {saving ? "Submitting…" : `Submit ${rows.length || ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
